import React, { useEffect, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useSearchParams } from 'react-router-dom';
import { getVision } from '../../actions/planningAction';
import { getReferralInfo } from '../../actions/userAction';
import config from '../../config';
import PlanningDashboard from './PlanningDashboard';
import PlanningChat from './PlanningChat';
import PlanningJournal from './PlanningJournal';
import PlanningExecute from './PlanningExecute';
import PlanningLibrary from './PlanningLibrary';
import PlanningHabits from './PlanningHabits';
import PlanningSettings from './PlanningSettings';
import { useTheme } from '../../context/ThemeContext';
import './PlanningWorkspace.css';

// Phase-2 kill switch: visions created BEFORE this cutoff keep full access —
// unchanged, see + edit as always. Any vision created at/after this instant
// is a "new" vision and is locked to the "coming soon" screen instead of the
// workspace. Flip PLANNING_LOCK_NEW_VISIONS to false (or push the cutoff) to
// reopen. Keep both in sync with PLANNING_LOCK_NEW_VISIONS / cutoff in
// projectMillionNew/server/controllers/planningController.js, which enforces
// the same freeze server-side.
const PLANNING_LOCK_NEW_VISIONS = true;
const PLANNING_LOCK_CUTOFF = new Date('2026-08-24T00:00:00.000Z');

const fmtStarted = (d) => d
  ? new Date(d).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
  : '';

// Phase-2 landing card for a locked (new) vision — this is the page most
// people will actually share, so it gets a real card layout instead of the
// bare centered stack the plain boot-intro uses. Manage vision (rename,
// visibility, delete) is always one click away via the gear in the topbar or
// the "Manage vision" link here — locking planning activity never locks that.
function LockedVisionCard({ vision, author, shareState, onShare, onManage, referState, onRefer }) {
  return (
    <div className="pw-lock-card">
      <div className="pw-lock-top">
        <span className="pw-lock-badge">Phase 2</span>
        <span className="pw-lock-tenure">{vision?.timeframe ? `${vision.timeframe}-year vision` : 'Vision'}</span>
      </div>

      <h1 className="pw-lock-title">{vision?.title}</h1>

      <div className="pw-lock-meta">
        <div className="pw-lock-meta-item">
          <span className="pw-lock-meta-k">Made by</span>
          <span className="pw-lock-meta-v">{author || 'You'}</span>
        </div>
        {fmtStarted(vision?.createdAt) && (
          <div className="pw-lock-meta-item">
            <span className="pw-lock-meta-k">Started</span>
            <span className="pw-lock-meta-v">{fmtStarted(vision.createdAt)}</span>
          </div>
        )}
      </div>

      <div className="pw-lock-status">
        <span className="pw-lock-status-dot" />
        Next step coming soon — we're building Phase 2
      </div>

      <div className="pw-lock-actions">
        <button className="pw-lock-share" onClick={onShare}>
          {shareState === 'copied' ? 'Link copied ✓' : shareState === 'shared' ? 'Shared ✓' : 'Share this vision'}
        </button>
        <button className="pw-lock-manage" onClick={onManage}>
          <i className="bx bx-cog" /> Manage vision
        </button>
      </div>
      {/* Reward copy: point value here (25 signup / 125 activation) must stay
          in sync with SIGNUP_POINTS/ACTIVATION_POINTS in
          projectMillionNew/server/utils/referralRewards.js. */}
      <div className="pw-lock-reward">
        <span className="pw-lock-reward-icon" aria-hidden="true"><i className="bx bxs-gift" /></span>
        <div className="pw-lock-reward-body">
          <p className="pw-lock-reward-text">
            Earn <strong>150 reward points</strong> per friend who joins from your link and creates their own vision — redeem them anytime for bonus AI requests.
          </p>
          <button type="button" className="pw-lock-refer-btn" onClick={onRefer}>
            {referState === 'copied' ? 'Link copied ✓' : referState === 'shared' ? 'Shared ✓' : 'Refer a friend'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Blank-canvas intro shown for a fresh vision: the vision node sits centered on an
// empty canvas until the user clicks "Start making this vision real", which forms
// the grid and reveals the workspace.
function VisionBootIntro({ vision, forming, onStart, locked, author, shareState, onShare, onManage, referState, onRefer }) {
  return (
    <div className={`pw-boot${forming ? ' pw-boot--forming' : ''}`}>
      <div className="pw-boot-grid" aria-hidden="true" />
      {/* energy rings emitted on start */}
      <div className="pw-boot-rings" aria-hidden="true">
        <span className="pw-boot-ring" />
        <span className="pw-boot-ring" />
        <span className="pw-boot-ring" />
      </div>

      {locked ? (
        <LockedVisionCard
          vision={vision}
          author={author}
          shareState={shareState}
          onShare={onShare}
          onManage={onManage}
          referState={referState}
          onRefer={onRefer}
        />
      ) : (
        <div className="pw-boot-center">
          <div className="pw-boot-node">
            <span className="pw-boot-node-dot" aria-hidden="true" />
            <div className="pw-boot-eyebrow">{vision?.timeframe ? `${vision.timeframe}-year vision` : 'Vision'}</div>
            <p className="pw-boot-title">{vision?.title}</p>
          </div>

          <div className="pw-boot-action">
            {!forming ? (
              <button className="pw-boot-cta" onClick={onStart}>
                Start planning
                <span className="pw-boot-cta-arrow" aria-hidden="true"></span>
              </button>
            ) : (
              <div className="pw-boot-status">
                <span className="pw-boot-status-dot" /> Constructing your workspace
              </div>
            )}
          </div>
        </div>
      )}

      {/* white flash at the moment the gears shift */}
      <div className="pw-boot-flash" aria-hidden="true" />
    </div>
  );
}

export default function PlanningWorkspace() {
  const { visionId } = useParams();
  const dispatch = useDispatch();
  const { activeVision, milestones, loading } = useSelector(s => s.planning);
  const { user } = useSelector(s => s.user);
  const { referralCode } = useSelector(s => s.referral);
  const { theme } = useTheme();
  const [shareState, setShareState] = useState('idle'); // 'idle' | 'copied' | 'shared'
  const [referState, setReferState] = useState('idle'); // 'idle' | 'copied' | 'shared'
  const [searchParams, setSearchParams] = useSearchParams();
  const [showChat,      setShowChat]      = useState(false);
  const [view,          setView]          = useState('dashboard');
  const [focusNode,      setFocusNode]      = useState(null);
  const [focusTodo,      setFocusTodo]      = useState(null);
  const [chatFocusNode,  setChatFocusNode]  = useState(null);
  const [refreshKey,     setRefreshKey]     = useState(0);

  // Fresh-vision intro: blank canvas + "start" button, then the grid forms.
  const bootKey = `dmyor_vision_booted_${visionId}`;
  const [bootDone, setBootDone] = useState(() => !!localStorage.getItem(bootKey));
  const [booting,  setBooting]  = useState(false);
  // Keep in sync if the vision (route param) changes without a remount.
  useEffect(() => { setBootDone(!!localStorage.getItem(bootKey)); setBooting(false); }, [bootKey]);
  const isEmpty   = (milestones?.length || 0) === 0;
  // Phase-2 freeze: visions created at/after the cutoff force the intro
  // overlay open forever (even if a ?view=execute deep link tries to jump
  // straight past it). Visions from before the cutoff are unaffected —
  // full see + edit access exactly as before.
  const isLockedVision = PLANNING_LOCK_NEW_VISIONS
    && !!activeVision?.createdAt
    && new Date(activeVision.createdAt) >= PLANNING_LOCK_CUTOFF;
  const showIntro = isLockedVision || (isEmpty && !bootDone);
  const navReady  = !showIntro || booting;  // nav/options reveal once booting begins

  // Referral code powers the reward-points copy on the share card below;
  // fetched once per session (see getReferralInfo's own dedupe check).
  useEffect(() => { dispatch(getReferralInfo()); }, [dispatch]);

  // Server-rendered share card (real OG/Twitter tags baked in server-side —
  // this in-app screen is client-rendered and crawlers never see its tags).
  // ?ref= attributes any resulting signup back to this user for reward points.
  const shareUrl = referralCode
    ? `${config.apiUrl}/share/vision/${visionId}?ref=${referralCode}`
    : `${config.apiUrl}/share/vision/${visionId}`;
  const shareVision = useCallback(async () => {
    const text = activeVision?.title ? `${activeVision.title} — on dmyor` : 'My vision on dmyor';
    if (navigator.share) {
      try {
        await navigator.share({ title: text, url: shareUrl });
        setShareState('shared');
      } catch (_) { return; } // user cancelled the native share sheet — not an error
    } else {
      try {
        await navigator.clipboard.writeText(shareUrl);
        setShareState('copied');
      } catch (_) { return; }
    }
    setTimeout(() => setShareState('idle'), 2200);
  }, [shareUrl, activeVision?.title]);

  // Generic invite link (not tied to this vision) — same signup?ref= link the
  // dedicated Referral page uses, so friends land on plain signup, not a
  // specific vision's share card.
  const referralLink = referralCode ? `${window.location.origin}/signup?ref=${referralCode}` : '';
  const referFriend = useCallback(async () => {
    if (!referralLink) return;
    if (navigator.share) {
      try {
        await navigator.share({ title: 'Join me on dmyor', url: referralLink });
        setReferState('shared');
      } catch (_) { return; }
    } else {
      try {
        await navigator.clipboard.writeText(referralLink);
        setReferState('copied');
      } catch (_) { return; }
    }
    setTimeout(() => setReferState('idle'), 2200);
  }, [referralLink]);

  const startVision = useCallback(() => {
    if (isLockedVision || booting) return;
    setBooting(true);
    try { localStorage.setItem(bootKey, '1'); } catch (_) {}
    // Let the grid-forming animation play, then hand over to the real workspace.
    setTimeout(() => { setBootDone(true); setBooting(false); setShowChat(true); }, 1650);
  }, [booting, bootKey, isLockedVision]);

  const openChatWithNode = useCallback((node) => {
    // node may be null (e.g. "Ask Dmyor Assist" with no parent) — open plain chat.
    setChatFocusNode(node && node._id ? { ...node, ts: Date.now() } : null);
    setShowChat(true);
  }, []);

  // On mobile the chat overlays the planner. When the user applies a suggestion,
  // briefly close the chat so they can see the node land, then reopen it.
  const peekPlannerOnApply = useCallback(() => {
    if (typeof window === 'undefined') return;
    if (!window.matchMedia('(max-width: 900px)').matches) return;
    setShowChat(false);
    setTimeout(() => setShowChat(true), 2000);
  }, []);

  // Deep-link from a doc: ?view=execute&todo=<id> opens Execute and focuses it.
  useEffect(() => {
    const v = searchParams.get('view');
    const todo = searchParams.get('todo');
    if (!v && !todo) return;
    if (v) setView(v);
    if (todo) setFocusTodo({ id: todo, ts: Date.now() });
    // Consume the params so refresh/back doesn't re-trigger.
    setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams]);

  const refresh = useCallback(() => {
    setRefreshKey(k => k + 1);
    return dispatch(getVision(visionId)).catch(() => {});
  }, [visionId, dispatch]);

  useEffect(() => {
    refresh();
  }, [visionId]);


  if (loading && !activeVision) {
    return <div className="pw-loading">Loading...</div>;
  }
  if (!activeVision) {
    return <div className="pw-loading">Vision not found.</div>;
  }

  return (
    <div className={`pw-page pw-page--${theme}`}>
      {/* Nav */}
      <div className="pw-nav">
        <div className={`pw-view-toggle${navReady ? '' : ' pw-nav-hidden'}`}>
          <button
            className={`pw-view-btn${view === 'dashboard' ? ' pw-view-btn--active' : ''}`}
            onClick={() => setView('dashboard')}
          >
            Plan
          </button>
          <button
            className={`pw-view-btn${view === 'execute' ? ' pw-view-btn--active' : ''}`}
            onClick={() => setView('execute')}
          >
            Execute
          </button>
          <button
            className={`pw-view-btn${view === 'habits' ? ' pw-view-btn--active' : ''}`}
            onClick={() => setView('habits')}
          >
            Habits
          </button>
          <button
            className={`pw-view-btn${view === 'library' ? ' pw-view-btn--active' : ''}`}
            onClick={() => setView('library')}
          >
            Library
          </button>
          <button
            className={`pw-view-btn${view === 'journal' ? ' pw-view-btn--active' : ''}`}
            onClick={() => setView('journal')}
          >
            Journal
          </button>
        </div>

        <div className="pw-nav-right">
          <button
            className={`pw-chat-toggle${!showChat && (milestones?.length || 0) === 0 ? ' pw-chat-toggle--glow' : ''}${navReady ? '' : ' pw-nav-hidden'}`}
            onClick={() => setShowChat(s => !s)}
          >
            {showChat ? 'Close D Assist' : 'D Assist'}
          </button>
          {/* Always reachable, even on a locked (Phase 2) vision — renaming,
              changing visibility, and deleting aren't "planning activity". */}
          <button
            className={`pw-settings-btn${view === 'settings' ? ' pw-settings-btn--active' : ''}`}
            onClick={() => setView(v => v === 'settings' ? 'dashboard' : 'settings')}
            title="Vision settings"
          >
            <i className="bx bx-cog" />
          </button>
        </div>
      </div>

      {/* Global vision time progress — sits on the nav/content boundary, visible in every mode */}
      {!showIntro && view !== 'settings' && activeVision?.timeframe && activeVision?.createdAt && (() => {
        const MS = 86400000;
        const total = activeVision.timeframe * 365;
        const passed = Math.floor((Date.now() - new Date(activeVision.createdAt)) / MS);
        const left = Math.max(total - passed, 0);
        const pct = Math.min(Math.max((passed / total) * 100, 0), 100);
        return (
          <div className="pw-timebar">
            <div className="pw-timebar-track">
              <div className="pw-timebar-fill" style={{ width: `${pct}%` }} />
            </div>
            <span className="pw-timebar-label">
              <strong>{left.toLocaleString()}</strong> days left · {Math.round(pct)}%
            </span>
          </div>
        );
      })()}

      <div className="pw-content">
        {showIntro && view !== 'settings' && (
          <VisionBootIntro
            vision={activeVision}
            forming={booting}
            onStart={startVision}
            locked={isLockedVision}
            onManage={() => setView('settings')}
            author={user?.name}
            shareState={shareState}
            onShare={shareVision}
            referState={referState}
            onRefer={referFriend}
          />
        )}
        {!showIntro && view === 'dashboard' && (
          <PlanningDashboard
            vision={activeVision}
            milestones={milestones}
            visionId={visionId}
            onRefresh={refresh}
            theme={theme}
            focusNode={focusNode}
            onOpenChatWithNode={openChatWithNode}
          />
        )}
        {!showIntro && view === 'execute' && (
          <PlanningExecute
            vision={activeVision}
            milestones={milestones}
            visionId={visionId}
            onRefresh={refresh}
            onFocusNode={(id) => { setFocusNode({ id, ts: Date.now() }); setView('dashboard'); }}
            focusTodo={focusTodo}
            theme={theme}
            externalRefreshKey={refreshKey}
          />
        )}
        {!showIntro && view === 'habits' && (
          <PlanningHabits
            visionId={visionId}
            milestones={milestones}
            theme={theme}
            onFocusNode={(id) => { setFocusNode({ id, ts: Date.now() }); setView('dashboard'); }}
            onGoToPlan={() => setView('dashboard')}
            externalRefreshKey={refreshKey}
          />
        )}
        {!showIntro && view === 'library' && (
          <PlanningLibrary
            visionId={visionId}
            milestones={milestones}
            theme={theme}
            externalRefreshKey={refreshKey}
          />
        )}
        {!showIntro && view === 'journal' && (
          <PlanningJournal
            vision={activeVision}
            visionId={visionId}
            theme={theme}
          />
        )}
        {view === 'settings' && (
          <PlanningSettings
            vision={activeVision}
            visionId={visionId}
            theme={theme}
            onUpdated={refresh}
            locked={isLockedVision}
          />
        )}

        {showChat && (
          <div className="pw-chat-drawer">
            <PlanningChat visionId={visionId} milestones={milestones} onRefresh={refresh} onFocusNode={(id) => { setFocusNode({ id, ts: Date.now() }); setView('dashboard'); }} onAfterApply={peekPlannerOnApply} initialAttachedNode={chatFocusNode} theme={theme} />
          </div>
        )}
      </div>
    </div>
  );
}
